import React, { useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { useTheme, useMediaQuery } from '@mui/material';
import { SimpleTreeView, TreeItem, TreeViewItemId } from '@mui/x-tree-view';
import { NavLink } from "react-router-dom";
import DefaultIcon from '@mui/icons-material/PlaylistPlayRounded';

import appConfig from '@/config';
import { setSidebarOpen } from '@/reducks/sidebar';
import { setExpandedItems } from '@/reducks/menu';
import { menuItems, type MenuItem as AppMenuItem } from '@/services/menu';

/**
 * サイドバー
 */
export default function Sidebar() {

  // サイドバー開閉状態
  const dispatch = useDispatch();
  const sidebarOpen = useSelector((state: any) => state.sidebar.open);
  const handleClose = useCallback(function() {
    dispatch(setSidebarOpen(false));
  }, []);

  // サイドバー表示方式
  // - 画面が小さい場合はフロート表示 (通常は固定表示)
  // - 固定表示の場合はアニメーションなし (duration=0)
  const theme = useTheme();
  const floating = useMediaQuery(theme.breakpoints.down('md'));
  const drawerVariant = floating ? 'temporary' : 'persistent';
  const drawerTransition = floating ? undefined : 0;

  // メニュー項目一覧
  const items = menuItems();

  // メニュー項目展開状態
  const expandedItems = useSelector((state: any) => state.menu.expandedItems);
  const handleExpandedChange = useCallback(function(
      event: React.SyntheticEvent | null,
      itemIds: Array<TreeViewItemId>) {
    dispatch(setExpandedItems(itemIds));
  }, []);

  //
  // サイドメニュー描画
  //
  return (
    <Drawer component="nav" id="sidebar"
      anchor="left"
      variant={drawerVariant}
      transitionDuration={drawerTransition}
      open={sidebarOpen}
      onClose={handleClose}
    >
      {/* パディング */}
      <Box sx={{
          flexGrow: 0, flexShrink: 0,
          height: `${appConfig.ui.headerHeight}px`,
        }}
      />

      {/* メニュー項目 */}
      <Box id="sidebar-menu"
        sx={{
          width: appConfig.ui.sidebarWidth,
          overflowY: 'auto',
          pt: 2, pb: 2,
        }}
      >
        {/* ツリーメニュー */}
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={handleExpandedChange}
        >
          {renderMenuItems(items, floating, handleClose)}
        </SimpleTreeView>
      </Box>
    </Drawer>
  );
}

/**
 * メニュー項目描画
 */
function renderMenuItems(
  items: AppMenuItem[],
  floating: boolean,
  handleClose: () => void,
): React.ReactNode {

  // 項目がなければ終了
  if (!items?.length) {
    return null;
  }

  // ツリー階層作成
  const treeItems = items.map((item) => {

    // 項目キー
    const key = `sidebar-${item.key}`;

    // 配下項目取得
    const children = renderMenuItems(
      item.children(),
      floating,
      handleClose
    );

    // 配下項目あり
    if (children) {
      return (

        // 親項目
        <TreeItem key={key} itemId={key} id={key}
          label={
            <Typography variant="subtitle2" noWrap component="div">
              {item.label}
            </Typography>
          }
        >
          {/* 子項目 */}
          {children}
        </TreeItem>
      );
    }

    // 配下項目なし - 末端のメニュー項目
    return (
      <TreeItem key={key} itemId={key} id={key}
        label={
          <SideMenuItem
            item={item}
            floating={floating}
            handleClose={handleClose}
          />
        }
        sx={{
          // インデントを狭くする (アイコン領域を減らす)
          '& .MuiTreeItem-iconContainer': { width: 0 },
        }}
      />
    );
  });
  return treeItems;
}

/**
 * サイドメニュー項目
 *
 * @param item メニュー項目
 * @param floating フロート表示中判定
 * @param handleClose フロート表示クローズハンドラ
 */
function SideMenuItem({
  item,
  floating,
  handleClose,
}: {
  item: AppMenuItem,
  floating: boolean,
  handleClose: () => void,
}) {
  if (!item?.link) {
    return null;
  }

  // デフォルトアイコン
  const itemIcon = item.icon ?? (
    <DefaultIcon color="primary" />
  );

  // メニュー項目
  return (
    <NavLink to={item.link}
      onClick={floating ? handleClose : undefined}
      style={{
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      {({ isActive }) => (
        <Stack direction="row" alignItems="center"
          sx={{

            // 表示中メニュー項目の右枠を着色
            borderRight: isActive ? 3 : 0,
            borderColor: 'primary.main',
          }}
        >
          {/* 項目アイコン */}   
          <Box style={{ display: 'flex' }}>
            {itemIcon}
          </Box>

          {/* 項目ラベル - 項目幅を超える場合は省略表示 */}
          <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1, ml: 0.2 }}>
            {item.label}
          </Typography>
        </Stack>
      )}
    </NavLink>
  );
}
